import { createAsyncThunk } from '@reduxjs/toolkit';
import getLocationId from '../selectors/getLocationId';
import getItems from '../selectors/getItems';
import { fetchPOSPoints } from './fetchPOSPoints';
import { setMemberKiosk } from './setMemberKiosk';
import { addToShadowCart } from '../operations';
import { FAILURE_REASON } from '../constants';
import { findPurchaseWithPLU } from '../utils/ordering/determineDiscountsForPurchase';
import { stagePurchaseFromItem } from './stagePurchaseFromItem';
import { addOffers } from '../reducers/currentOrder/offers';
import { setSelectedOffer } from '../reducers/currentOrder/selectedOffer';
import { setOfferLockout } from './setOfferLockout';
import { isOfferLockedOut } from '../utils/ordering/isOfferLockedOut';
import { selectOffer } from './selectOffer';
import { getStagedPurchases } from '../selectors';
import { OFFER_TYPE } from '../constants/offerType';

export const applyBarcode = createAsyncThunk(
  '$applyBarcode',
  async (
    data: {
      barcode: string;
      locationId?: string;
      skipEnforcedPluItem?: boolean;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const { barcode, locationId: actionLocationId, skipEnforcedPluItem } = data;

    let reason: ApplyBarcodeFailureReason;

    try {
      const locationId =
        actionLocationId || getLocationId(getState() as EntireFrontendState);

      if (!locationId) {
        throw new Error('no location id');
      }

      const { member, offer, giftCard, itemDetails } = await dispatch(
        fetchPOSPoints({ barcode, locationId }),
      ).unwrap();

      if (!member && !giftCard && !offer) {
        reason = FAILURE_REASON.MEMBER_DETAILS_NOT_FOUND;

        throw new Error("valid barcode but member doesn't exist");
      }

      // Apply offer BEFORE member login to prevent a race where setMemberKiosk →
      // fetchLocationOffers → addOffers fires applyAutomaticOffer concurrently with
      // selectOffer, overwriting the purchaseId set here.
      if (offer) {
        const selectedOffer = (getState() as EntireFrontendState).ordering
          .currentOrder.selectedOffer;

        if (selectedOffer.offerId !== offer.id) {
          const isLockedOut = isOfferLockedOut({
            offer,
            currentTime: new Date(),
          });
          if (isLockedOut) {
            reason = FAILURE_REASON.OFFER_LOCKED_OUT;
            throw new Error('Offer is currently assigned to another cart');
          }
        }

        dispatch(addOffers([offer]));
        await dispatch(selectOffer({ offerId: offer.id, userAdded: true, skipEnforcedPluItem })).unwrap();
        // selectOffer sets offerId/purchaseId but not hash; restore hash for lockout tracking
        const { purchaseId } = (getState() as EntireFrontendState).ordering.currentOrder.selectedOffer;
        dispatch(setSelectedOffer({ offerId: offer.id, purchaseId: purchaseId ?? undefined, userAdded: true, hash: offer.hash }));
        dispatch(setOfferLockout({ hash: offer.hash }));

        // For PERCENTAGE_OFF_ITEM_POS_ENFORCED (type 13), the target PLU must be in cart.
        // skipEnforcedPluItem is set when scanning from the welcome screen — offer is still
        // applied but the item is not staged (no cart on that screen yet).
        // Use the same shadow-cart pattern as itemDetails: if currentOrder.menu is not yet
        // populated (pre-applyBuffer on kiosk), add to shadow cart so applyShadowOrder
        // restores the item after applyBuffer clears stagedPurchases.
        if (
          !skipEnforcedPluItem &&
          offer.type === OFFER_TYPE.PERCENTAGE_OFF_ITEM_POS_ENFORCED &&
          offer.targetPLUs[0]
        ) {
          const targetPLU = offer.targetPLUs[0];
          const currentItems = getItems(getState() as EntireFrontendState);

          if (!currentItems) {
            dispatch(
              addToShadowCart([
                { plucode: targetPLU, name: offer.name, quantity: 1, choiceSets: {} },
              ]),
            );
          } else {
            const stagedPurchases = getStagedPurchases(
              getState() as EntireFrontendState,
            );
            const alreadyInCart = findPurchaseWithPLU(stagedPurchases, targetPLU);

            if (!alreadyInCart) {
              const targetKey = Object.entries(currentItems).find(
                ([, item]) => item.plucode === targetPLU,
              )?.[0];

              if (targetKey) {
                const newPurchaseId = await dispatch(
                  stagePurchaseFromItem({ itemId: targetKey, force: true }),
                ).unwrap();
                dispatch(
                  setSelectedOffer({
                    offerId: offer.id,
                    purchaseId: newPurchaseId,
                    userAdded: true,
                    hash: offer.hash,
                  }),
                );
              }
            }
          }
        }
      }

      if (member) {
        await dispatch(setMemberKiosk({ member }));
      }

      let itemAdded;

      if (itemDetails) {
        const allItems: Items | undefined = getItems(
          getState() as EntireFrontendState,
        );

        if (!allItems) {
          // no menu data yet, add item to shadow cart

          const shadowItem: ShadowItem = {
            plucode: itemDetails.id,
            name: itemDetails.name,
            quantity: 1,
            choiceSets: {},
          };

          dispatch(addToShadowCart([shadowItem]));
        } else {
          const itemInMenuData = allItems[itemDetails.id];

          if (!itemInMenuData) {
            reason = FAILURE_REASON.PLU_NOT_FOUND;

            throw new Error('could not find item');
          }

          await dispatch(stagePurchaseFromItem({ itemId: itemDetails.id }));
          itemAdded = true;
        }
      }

      return {
        member,
        offer,
        giftCard,
        itemDetails,
        itemAdded,
      };
    } catch (e) {
      console.error('Apply barcode failed', { e, reason });
      return rejectWithValue({ e, reason });
    }
  },
);
